let cached: boolean | undefined;
let cachedRenderer: string | null | undefined;

/**
 * Probes for a GPU-accelerated WebGL context. `failIfMajorPerformanceCaveat` makes the browser
 * refuse a context when it would fall back to a software rasterizer (SwiftShader, llvmpipe), which
 * is what headless audit machines like PageSpeed Insights and GPU-less devices get. Rendering the
 * three.js scenes there pegs the main thread for seconds, so they are skipped instead.
 *
 * The probe context is released straight away and the result is cached for the session.
 *
 * @returns True when a hardware-accelerated WebGL context is available; false during SSR
 */
export function hasHardwareWebGL(): boolean {
  if ('undefined' === typeof document) {
    return false;
  }

  if (undefined !== cached) {
    return cached;
  }

  try {
    const canvas = document.createElement('canvas');
    const options: WebGLContextAttributes = { failIfMajorPerformanceCaveat: true };
    const gl: WebGLRenderingContext | WebGL2RenderingContext | null = canvas.getContext('webgl2', options) ?? canvas.getContext('webgl', options);
    cached = null !== gl;

    const debugInfo = gl?.getExtension('WEBGL_debug_renderer_info');
    cachedRenderer = debugInfo ? (gl?.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) as string) : null;

    gl?.getExtension('WEBGL_lose_context')?.loseContext();
  } catch {
    cached = false;
    cachedRenderer = null;
  }

  return cached;
}

/**
 * Reads the unmasked GPU renderer string captured during the same probe as `hasHardwareWebGL`,
 * triggering that probe if it has not run yet so both results come from a single WebGL context.
 *
 * @returns The UNMASKED_RENDERER_WEBGL string captured during the hardware probe, or null when
 *          the probe failed, ran during SSR, or the WEBGL_debug_renderer_info extension is absent
 */
export function getWebGLRendererString(): string | null {
  if ('undefined' === typeof document) {
    return null;
  }

  if (undefined === cachedRenderer) {
    hasHardwareWebGL();
  }

  return cachedRenderer ?? null;
}
